/**
 * §7.4 帳號管理（新增）。管理者的落點（§7.2）。
 *
 * §5.1 **帳號只停用、不刪除**——事件表引用 actor_user_id，刪帳號會破壞稽核鏈。
 * §6.2 一個帳號可以有多個角色，而且角色**依廠區授予**（§12.5）。
 */

import { useState } from 'react'
import { ApiError, api, withQuery } from '../../api/client.js'
import { useQuery } from '../../api/hooks.js'
import { Alert, Button, Card, Empty, Field, Mono, Pill, Select, Table, Td, TextInput } from '../../components/ui.js'
import { ts } from '../../../../shared/lib/format.js'

interface UserRow {
  id: number
  email: string
  displayName: string
  employeeNo: string | null
  status: 'active' | 'disabled'
  hasPassword: boolean
  mustChangePassword: boolean
  defaultSiteId: string | null
  defaultStationId: string | null
  lockedUntil: string | null
  lastLoginAt: string | null
  grants: { siteId: string; roleCode: string; roleName: string }[]
}

interface GrantOptions {
  roles: { code: string; name: string; description: string | null; isBuiltin: boolean; allSitesOnly: boolean }[]
  sites: { siteId: string; name: string; shortName: string }[]
}

export function AdminAccounts() {
  const [includeDisabled, setIncludeDisabled] = useState(false)
  const users = useQuery<{ items: UserRow[] }>(withQuery('/api/admin/users', { includeDisabled }))
  const options = useQuery<GrantOptions>('/api/admin/grant-options')
  const [notice, setNotice] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<UserRow | null>(null)

  const act = async (fn: () => Promise<void>) => {
    try { await fn(); users.reload(); setNotice(null) } catch (e) {
      setNotice({ tone: 'danger', text: e instanceof ApiError ? e.message : String(e) })
    }
  }

  return (
    <div className="space-y-4">
      {notice && <Alert tone={notice.tone === 'ok' ? 'ok' : 'danger'}>{notice.text}</Alert>}

      <Card
        title="帳號管理"
        subtitle="帳號只停用、不刪除——事件表引用操作人，刪帳號會破壞稽核鏈"
        right={<Button size="sm" variant="primary" onClick={() => setCreating(true)}>建立帳號</Button>}
      >
        <label className="mb-3 flex items-center gap-1.5 text-sm text-ink-700">
          <input type="checkbox" checked={includeDisabled}
            onChange={(e) => setIncludeDisabled(e.target.checked)} />
          顯示已停用的帳號
        </label>

        {users.error && <Alert tone="danger" title="讀不到帳號">{users.error.message}</Alert>}
        {(users.data?.items ?? []).length === 0 && !users.loading && <Empty>沒有帳號。</Empty>}

        {(users.data?.items ?? []).length > 0 && (
          <Table head={['名稱', 'Email', '狀態', '角色（依廠區）', '最後登入', '']}>
            {users.data!.items.map((u) => (
              <tr key={u.id} className={u.status === 'disabled' ? 'text-ink-400' : ''}>
                <Td>
                  {u.displayName}
                  {u.employeeNo && <span className="ml-1 text-xs text-ink-400">#{u.employeeNo}</span>}
                </Td>
                <Td className="text-xs"><Mono>{u.email}</Mono></Td>
                <Td>
                  {u.status === 'active' ? <Pill tone="emerald">啟用</Pill> : <Pill tone="ink">已停用</Pill>}
                  {!u.hasPassword && <Pill tone="amber" title="只能用 Email 驗證碼登入">無密碼</Pill>}
                  {u.mustChangePassword && <Pill tone="amber">待改密碼</Pill>}
                  {u.lockedUntil && new Date(u.lockedUntil) > new Date() && <Pill tone="red">已鎖定</Pill>}
                </Td>
                <Td className="text-xs">
                  {u.grants.length === 0
                    ? <span className="text-amber-700">未指派（登入後什麼都做不了）</span>
                    : u.grants.map((g) => (
                        <span key={`${g.siteId}-${g.roleCode}`} className="mr-1.5 inline-block">
                          <Mono>{g.siteId === '*' ? '全廠區' : g.siteId}</Mono>
                          <span className="text-ink-500">:{g.roleName}</span>
                        </span>
                      ))}
                </Td>
                <Td className="text-xs">{u.lastLoginAt ? ts(u.lastLoginAt) : '從未登入'}</Td>
                <Td>
                  <div className="flex flex-wrap gap-1.5">
                    <Button size="sm" onClick={() => setEditing(u)}>角色</Button>
                    <Button size="sm" onClick={() => void act(async () => {
                      const r = await api.post<{ oneTimePassword: string }>(
                        `/api/admin/users/${u.id}/password-reset`)
                      setNotice({ tone: 'ok', text: `${u.displayName} 的一次性密碼：${r.oneTimePassword}（只顯示這一次，請立即抄下）` })
                    })}>重設密碼</Button>
                    {u.lockedUntil && new Date(u.lockedUntil) > new Date() && (
                      <Button size="sm" onClick={() => void act(async () => {
                        await api.post(`/api/admin/users/${u.id}/unlock`)
                      })}>解鎖</Button>
                    )}
                    <Button
                      size="sm"
                      variant={u.status === 'active' ? 'danger' : 'secondary'}
                      onClick={() => void act(async () => {
                        await api.patch(`/api/admin/users/${u.id}`, {
                          status: u.status === 'active' ? 'disabled' : 'active',
                        })
                      })}
                    >{u.status === 'active' ? '停用' : '重新啟用'}</Button>
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {creating && (
        <CreateUser
          onClose={() => setCreating(false)}
          onDone={(text) => { setCreating(false); setNotice({ tone: 'ok', text }); users.reload() }}
        />
      )}

      {editing && options.data && (
        <EditGrants
          user={editing}
          options={options.data}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); users.reload() }}
        />
      )}
    </div>
  )
}

function CreateUser({ onClose, onDone }: { onClose: () => void; onDone: (text: string) => void }) {
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [employeeNo, setEmployeeNo] = useState('')
  const [generatePassword, setGeneratePassword] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const r = await api.post<{ user: UserRow; oneTimePassword: string | null }>('/api/admin/users', {
        email: email.trim(), displayName: displayName.trim(),
        employeeNo: employeeNo.trim() || null, generatePassword,
      })
      onDone(r.oneTimePassword
        ? `已建立 ${r.user.displayName}。一次性密碼：${r.oneTimePassword}（只顯示這一次，請立即抄下）`
        : `已建立 ${r.user.displayName}（未設密碼，只能用 Email 驗證碼登入）`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err))
    } finally { setBusy(false) }
  }

  return (
    <Modal title="建立帳號" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Email" hint="這就是登入帳號，大小寫不敏感" required>
          <TextInput type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="顯示名稱" hint="稽核紀錄上會出現的名字，請填真實姓名" required>
          <TextInput value={displayName} onChange={(e) => setDisplayName(e.target.value)}
            placeholder="操作員 王小明" />
        </Field>
        <Field label="工號" hint="選填">
          <TextInput value={employeeNo} onChange={(e) => setEmployeeNo(e.target.value)} />
        </Field>
        <label className="flex items-start gap-2 text-sm text-ink-700">
          <input type="checkbox" checked={generatePassword} className="mt-1"
            onChange={(e) => setGeneratePassword(e.target.checked)} />
          <span>
            產生一次性密碼
            <span className="block text-xs text-ink-500">
              不勾的話這個帳號沒有密碼，只能用 Email 驗證碼登入（適合偶爾來看報表的管理層）。
            </span>
          </span>
        </label>
        {error && <Alert tone="danger">{error}</Alert>}
        <p className="text-xs text-ink-500">
          建立後記得指派角色——沒有角色的帳號登入後什麼都做不了。
        </p>
        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={busy} className="flex-1">
            {busy ? '請稍候…' : '建立'}
          </Button>
          <Button type="button" onClick={onClose}>取消</Button>
        </div>
      </form>
    </Modal>
  )
}

/**
 * §12.5 角色依廠區授予。
 * §6.2 `group_admin` 這類集團層級角色**只能以「全廠區」授予**——
 * 後端會回 `GROUP_ROLE_REQUIRES_ALL_SITES`，這裡先在畫面上擋掉，
 * 讓人不用送出去才知道不行。
 */
function EditGrants({ user, options, onClose, onDone }: {
  user: UserRow
  options: GrantOptions
  onClose: () => void
  onDone: () => void
}) {
  const [grants, setGrants] = useState(user.grants.map((g) => ({ siteId: g.siteId, roleCode: g.roleCode })))
  const [siteId, setSiteId] = useState(options.sites[1]?.siteId ?? '*')
  const [roleCode, setRoleCode] = useState(options.roles[0]?.code ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const role = options.roles.find((r) => r.code === roleCode)
  const mustBeAllSites = role?.allSitesOnly === true
  const effectiveSite = mustBeAllSites ? '*' : siteId
  const duplicate = grants.some((g) => g.siteId === effectiveSite && g.roleCode === roleCode)

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.post(`/api/admin/users/${user.id}/roles`, { grants })
      onDone()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally { setBusy(false) }
  }

  return (
    <Modal title={`${user.displayName} 的角色`} onClose={onClose}>
      <p className="mb-3 text-xs text-ink-500">
        角色依廠區授予。在某個廠區的有效權限 = 該廠區的角色 ∪ 全廠區的角色。
        變更後這個帳號的所有登入工作階段會立即失效，並記入認證稽核。
      </p>

      {grants.length === 0 ? <Empty>還沒有任何授權。</Empty> : (
        <ul className="mb-3 space-y-1.5">
          {grants.map((g, i) => {
            const r = options.roles.find((x) => x.code === g.roleCode)
            const s = options.sites.find((x) => x.siteId === g.siteId)
            return (
              <li key={`${g.siteId}-${g.roleCode}`}
                className="flex items-center justify-between rounded border border-ink-200 px-2.5 py-1.5 text-sm">
                <span>
                  <Mono>{s?.shortName ?? g.siteId}</Mono>
                  <span className="mx-1.5 text-ink-400">·</span>
                  {r?.name ?? g.roleCode}
                </span>
                <Button size="sm" variant="ghost"
                  onClick={() => setGrants((gs) => gs.filter((_, j) => j !== i))}>移除</Button>
              </li>
            )
          })}
        </ul>
      )}

      <div className="rounded-md border border-ink-200 bg-ink-50 p-3 space-y-2">
        <Field label="角色">
          <Select value={roleCode} onChange={(e) => setRoleCode(e.target.value)}>
            {options.roles.map((r) => (
              <option key={r.code} value={r.code}>{r.name}（{r.code}）</option>
            ))}
          </Select>
        </Field>
        {role?.description && <p className="text-xs text-ink-500">{role.description}</p>}
        <Field label="廠區" hint={mustBeAllSites
          ? '這是集團層級角色，只能以「全廠區」授予——建立廠區是集團層級的動作。'
          : undefined}>
          <Select value={effectiveSite} disabled={mustBeAllSites}
            onChange={(e) => setSiteId(e.target.value)}>
            {options.sites.map((s) => (
              <option key={s.siteId} value={s.siteId}>{s.name}</option>
            ))}
          </Select>
        </Field>
        <Button
          size="sm"
          disabled={!roleCode || duplicate}
          onClick={() => setGrants((gs) => [...gs, { siteId: effectiveSite, roleCode }])}
        >{duplicate ? '已經有這筆授權' : '加入'}</Button>
      </div>

      {error && <div className="mt-3"><Alert tone="danger">{error}</Alert></div>}

      <div className="mt-4 flex gap-2">
        <Button variant="primary" onClick={() => void save()} disabled={busy} className="flex-1">
          {busy ? '請稍候…' : '儲存'}
        </Button>
        <Button onClick={onClose}>取消</Button>
      </div>
    </Modal>
  )
}

function Modal({ title, children, onClose }: {
  title: string; children: React.ReactNode; onClose: () => void
}) {
  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-ink-950/50 p-4">
      <div className="mt-8 w-full max-w-md rounded-lg border border-ink-200 bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-start justify-between gap-3">
          <h2 className="font-semibold text-ink-900">{title}</h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700" aria-label="關閉">✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}
